/* ══════════════════════════════════════════════════════════════════════════════
   STYLES
══════════════════════════════════════════════════════════════════════════════ */
const DiscordPageStyles = `
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Outfit:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;600&display=swap');

  .dsc *, .dsc *::before, .dsc *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .dsc {
    --ink:           #0b0c10;
    --paper:         #0d0e14;
    --surface:       #13141b;
    --surface2:      #1a1b24;
    --rim:           rgba(255,255,255,.06);
    --rim2:          rgba(255,255,255,.11);
    --blurple:       #5865f2;
    --blurple2:      #7289da;
    --blurple-glow:  rgba(88,101,242,.22);
    --gold:          #f5c842;
    --mint:          #23d18b;
    --ice:           #00d4ff;
    --t0: #ffffff;
    --t1: #e8e9f0;
    --t2: #9395a5;
    --t3: #4e5068;
    --ff-display: 'Bebas Neue', sans-serif;
    --ff-body:    'Outfit', sans-serif;
    --ff-mono:    'JetBrains Mono', monospace;

    display: block;
    min-height: 100vh;
    background: var(--paper);
    color: var(--t1);
    font-family: var(--ff-body);
    overflow-x: hidden;
    line-height: 1;
  }

  /* Grain */
  .dsc-grain {
    position: fixed; inset: 0;
    pointer-events: none; z-index: 0; opacity: .025;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
    background-size: 200px;
  }

  /* ── HERO ─────────────────────────────────────────────────────────────── */
  .dsc-hero {
    position: relative;
    min-height: 100vh;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 7rem 1.5rem 5rem;
    text-align: center; overflow: hidden;
  }
  .dsc-aurora { position: absolute; inset: 0; pointer-events: none; overflow: hidden; }
  .dsc-orb {
    position: absolute; border-radius: 50%; filter: blur(80px);
    animation: orb 12s ease-in-out infinite alternate;
  }
  .dsc-orb:nth-child(1) {
    width:600px;height:400px; top:-10%;left:10%;
    background: radial-gradient(circle,rgba(88,101,242,.25) 0%,transparent 70%);
  }
  .dsc-orb:nth-child(2) {
    width:500px;height:500px; top:20%;right:-5%;
    background: radial-gradient(circle,rgba(0,212,255,.12) 0%,transparent 70%);
    animation-delay:-4s;
  }
  .dsc-orb:nth-child(3) {
    width:350px;height:350px; bottom:10%;left:30%;
    background: radial-gradient(circle,rgba(245,200,66,.1) 0%,transparent 70%);
    animation-delay:-8s;
  }
  @keyframes orb {
    0%   { transform: translate(0,0) scale(1); }
    100% { transform: translate(30px,-20px) scale(1.08); }
  }
  .dsc-grid {
    position: absolute; inset: 0; pointer-events: none;
    background-image:
      linear-gradient(rgba(88,101,242,.04) 1px,transparent 1px),
      linear-gradient(90deg,rgba(88,101,242,.04) 1px,transparent 1px);
    background-size: 60px 60px;
    mask-image: radial-gradient(ellipse 80% 80% at 50% 50%,black 30%,transparent 100%);
  }

  .dsc-hero-inner { position: relative; z-index: 2; max-width: 860px; }

  .dsc-live-badge {
    display: inline-flex; align-items: center; gap: .5rem;
    padding: .38rem 1rem .38rem .6rem; border-radius: 999px;
    border: 1px solid rgba(35,209,139,.3);
    background: rgba(35,209,139,.07);
    font-family: var(--ff-mono); font-size: .7rem; font-weight: 600;
    color: var(--mint); letter-spacing: .08em; text-transform: uppercase;
    margin-bottom: 2.4rem;
    animation: fade-up .6s cubic-bezier(.16,1,.3,1) both;
  }
  .dsc-live-dot {
    width:8px;height:8px;border-radius:50%;
    background:var(--mint);box-shadow:0 0 12px var(--mint);
    animation: live-pulse 1.8s ease-in-out infinite;
  }
  @keyframes live-pulse {
    0%,100% { opacity:1;transform:scale(1);box-shadow:0 0 12px var(--mint); }
    50%      { opacity:.6;transform:scale(.8);box-shadow:0 0 4px var(--mint); }
  }

  .dsc-h1 {
    font-family: var(--ff-display);
    font-size: clamp(4.5rem,13vw,9rem);
    line-height: .88;
    letter-spacing: .01em;
    color: var(--t0);
    margin-bottom: 1.3rem;
    animation: fade-up .8s cubic-bezier(.16,1,.3,1) .1s both;
  }
  .dsc-h1 .stroke {
    -webkit-text-stroke: 2px var(--blurple);
    color: transparent;
    display: block;
  }
  .dsc-h1 .grd {
    background: linear-gradient(90deg,var(--blurple),var(--ice));
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text; display: block;
  }

  .dsc-hero-sub {
    font-size: clamp(.95rem,2vw,1.18rem);
    font-weight: 300; color: var(--t2);
    line-height: 1.78; max-width: 520px;
    margin: 0 auto 2.8rem;
    animation: fade-up .8s cubic-bezier(.16,1,.3,1) .2s both;
  }
  .dsc-hero-sub strong { color: var(--t1); font-weight: 600; }

  .dsc-cta-row {
    display: flex; align-items: center;
    justify-content: center; gap: 1rem; flex-wrap: wrap;
    animation: fade-up .8s cubic-bezier(.16,1,.3,1) .3s both;
  }

  @keyframes fade-up {
    from { opacity:0; transform:translateY(18px); }
    to   { opacity:1; transform:translateY(0); }
  }

  .dsc-btn-join {
    display: inline-flex; align-items: center; gap: .6rem;
    padding: .95rem 2.1rem; border-radius: 12px;
    background: var(--blurple); color: #fff;
    font-family: var(--ff-body); font-size: 1rem; font-weight: 600;
    border: none; cursor: pointer; text-decoration: none;
    box-shadow: 0 4px 28px rgba(88,101,242,.4);
    transition: all .22s cubic-bezier(.16,1,.3,1);
    position: relative; overflow: hidden;
  }
  .dsc-btn-join::before {
    content:''; position:absolute; inset:0;
    background: linear-gradient(135deg,rgba(255,255,255,.13) 0%,transparent 60%);
  }
  .dsc-btn-join:hover {
    transform: translateY(-3px) scale(1.03);
    box-shadow: 0 0 60px rgba(88,101,242,.5), 0 8px 40px rgba(88,101,242,.5);
  }
  .dsc-btn-ghost {
    display: inline-flex; align-items: center; gap: .5rem;
    padding: .95rem 1.7rem; border-radius: 12px;
    background: var(--surface); color: var(--t2);
    font-family: var(--ff-body); font-size: .95rem; font-weight: 500;
    border: 1px solid var(--rim2); cursor: pointer; text-decoration: none;
    transition: all .22s;
  }
  .dsc-btn-ghost:hover { color:var(--t1); border-color:var(--blurple); background:rgba(88,101,242,.07); }

  /* Stats strip */
  .dsc-stats {
    display: grid; grid-template-columns: repeat(3,1fr);
    max-width: 680px; width: 100%; margin: 4rem auto 0;
    border: 1px solid var(--rim2); border-radius: 18px;
    background: var(--surface); overflow: hidden;
    animation: fade-up .8s cubic-bezier(.16,1,.3,1) .4s both;
  }
  .dsc-stat {
    padding: 1.9rem 1rem; text-align: center;
    border-right: 1px solid var(--rim); position: relative;
  }
  .dsc-stat:last-child { border-right: none; }
  .dsc-stat-n {
    font-family: var(--ff-display);
    font-size: 3rem; letter-spacing: .04em;
    color: var(--t0); line-height: 1;
  }
  .dsc-stat-n .c { color: var(--blurple); }
  .dsc-stat-lbl {
    font-family: var(--ff-mono); font-size: .66rem;
    color: var(--t3); text-transform: uppercase;
    letter-spacing: .1em; margin-top: .55rem;
  }
  .dsc-stat-shine {
    position: absolute; bottom: 0; left: 50%; transform: translateX(-50%);
    width: 50%; height: 1.5px; border-radius: 99px;
    background: linear-gradient(90deg,transparent,var(--blurple),transparent);
    opacity: 0; transition: opacity .3s;
  }
  .dsc-stat:hover .dsc-stat-shine { opacity: 1; }

  /* ── SECTION ──────────────────────────────────────────────────────────── */
  .dsc-section {
    position: relative; z-index: 1;
    max-width: 1080px; margin: 0 auto;
    padding: 6rem 1.5rem;
  }
  .dsc-eyebrow {
    display: inline-flex; align-items: center; gap: .5rem;
    font-family: var(--ff-mono); font-size: .68rem; font-weight: 600;
    color: var(--blurple2); text-transform: uppercase; letter-spacing: .14em;
    margin-bottom: 1.1rem;
  }
  .dsc-eyebrow::before {
    content:''; display:block; width:24px; height:1.5px;
    background:var(--blurple); border-radius:2px;
  }
  .dsc-h2 {
    font-family: var(--ff-display);
    font-size: clamp(2.6rem,5.5vw,4.5rem);
    letter-spacing: .02em; color: var(--t0);
    line-height: .95; margin-bottom: 1rem;
  }
  .dsc-h2 .hi {
    background: linear-gradient(90deg,var(--blurple2),var(--ice));
    -webkit-background-clip: text; -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .dsc-section-sub {
    font-size: 1.02rem; font-weight: 300; color: var(--t2);
    line-height: 1.8; max-width: 520px; margin-bottom: 3.5rem;
  }

  /* ── HIERARCHY ────────────────────────────────────────────────────────── */
  .dsc-hier { display: flex; flex-direction: column; gap: 0; max-width: 860px; }
  .dsc-wide { max-width: 680px; }
  .dsc-section-sub strong { color: var(--t1); font-weight: 600; }
  .dsc-h2 .gold { color: var(--gold); }
  .dsc-note {
    max-width: 860px; margin-top: 2rem;
    font-size: .9rem; font-weight: 300; line-height: 1.75; color: var(--t2);
  }
  .dsc-note strong { color: var(--t1); font-weight: 600; }
  .dsc-inline-link { color: var(--blurple2); text-decoration: none; font-weight: 500; white-space: nowrap; }
  .dsc-inline-link:hover { color: var(--ice); }

  .dsc-tier {
    display: grid; grid-template-columns: 68px 1fr; gap: 1.5rem;
    align-items: start;
    padding: 2.2rem 2.2rem 2.2rem 2rem;
    border-radius: 16px; border: 1px solid var(--rim);
    background: var(--surface); position: relative; overflow: hidden;
    transition: border-color .25s, box-shadow .25s, transform .25s;
  }
  .dsc-tier::before {
    content:''; position:absolute; top:0;left:0;right:0; height:1px;
    background:linear-gradient(90deg,transparent,var(--blurple),transparent);
    opacity:0; transition:opacity .3s;
  }
  .dsc-tier:hover {
    border-color: rgba(88,101,242,.4);
    box-shadow: 0 0 60px rgba(88,101,242,.07), inset 0 0 40px rgba(88,101,242,.03);
    transform: translateX(7px);
  }
  .dsc-tier:hover::before { opacity:1; }

  /* Role tones — one rgb triplet per role, shared by every section that draws one */
  .tone-teal   { --tone: #2dd4bf; --tone-rgb: 45,212,191; }
  .tone-blue   { --tone: #60a5fa; --tone-rgb: 96,165,250; }
  .tone-purple { --tone: #a78bfa; --tone-rgb: 167,139,250; }
  .tone-gold   { --tone: #f5c842; --tone-rgb: 245,200,66; }
  .tone-red    { --tone: #ff6b8a; --tone-rgb: 255,107,138; }

  .dsc-tier { --tone: var(--blurple2); --tone-rgb: 88,101,242; }
  .dsc-tier:hover {
    border-color: rgba(var(--tone-rgb),.45);
    box-shadow: 0 0 60px rgba(var(--tone-rgb),.08), inset 0 0 40px rgba(var(--tone-rgb),.03);
  }
  .dsc-tier::before { background:linear-gradient(90deg,transparent,var(--tone),transparent); }

  .dsc-tier-num {
    width:68px;height:68px;border-radius:14px;
    display:flex;align-items:center;justify-content:center;
    flex-shrink:0;position:relative;
    background:rgba(var(--tone-rgb),.08);
    border:1px solid rgba(var(--tone-rgb),.24);
  }
  .dsc-tier-img { width:42px;height:42px;object-fit:contain;filter:drop-shadow(0 2px 8px rgba(var(--tone-rgb),.35)); }
  .dsc-tier-step {
    position:absolute;top:-8px;left:-8px;
    min-width:24px;padding:.18rem .3rem;border-radius:7px;text-align:center;
    background:var(--paper);border:1px solid rgba(var(--tone-rgb),.35);
    font-family:var(--ff-mono);font-size:.58rem;font-weight:600;color:var(--tone);
  }

  .dsc-tier-pill {
    display:inline-block;font-family:var(--ff-mono);font-size:.63rem;font-weight:600;
    padding:.18rem .55rem;border-radius:999px;
    text-transform:uppercase;letter-spacing:.08em;
    margin-left:.65rem;vertical-align:middle;
    background:rgba(var(--tone-rgb),.12);color:var(--tone);
    border:1px solid rgba(var(--tone-rgb),.28);
  }

  .dsc-tier-title {
    font-family:var(--ff-display);font-size:1.65rem;
    letter-spacing:.04em;color:var(--t0);
    margin-bottom:.65rem;line-height:1;
  }
  .dsc-tier-desc {
    font-size:.93rem;font-weight:300;color:var(--t2);
    line-height:1.75;margin-bottom:1.1rem;
  }
  .dsc-tier-desc strong { color:var(--t1);font-weight:600; }

  .dsc-chips { display:flex;flex-wrap:wrap;gap:.4rem; }
  .dsc-chip {
    font-family:var(--ff-mono);font-size:.64rem;
    padding:.22rem .65rem;border-radius:7px;
    background:rgba(255,255,255,.04);color:var(--t3);
    border:1px solid var(--rim);letter-spacing:.04em;
  }

  .dsc-connector {
    display:flex;align-items:center;gap:.8rem;
    padding:.45rem 0 .45rem 34px;
    color:var(--t3);font-family:var(--ff-mono);
    font-size:.66rem;letter-spacing:.08em;
  }
  .dsc-cl { width:1.5px;height:30px;border-radius:2px;flex-shrink:0;
    background:linear-gradient(to bottom,var(--blurple),transparent); }

  /* ── DIVIDER ──────────────────────────────────────────────────────────── */
  .dsc-hr { max-width:1080px;margin:0 auto;padding:0 1.5rem; }
  .dsc-hr-line {
    height:1px;
    background:linear-gradient(90deg,transparent,var(--rim2) 30%,var(--rim2) 70%,transparent);
  }

  /* ── BOTS ─────────────────────────────────────────────────────────────── */
  .dsc-bots { display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:1.5rem; }
  .dsc-bot {
    padding:2.2rem;border-radius:16px;
    border:1px solid var(--rim);background:var(--surface);
    position:relative;overflow:hidden;
    transition:border-color .25s,box-shadow .25s;
  }
  .dsc-bot::after {
    content:'';position:absolute;top:-60px;right:-60px;
    width:200px;height:200px;border-radius:50%;
    background:radial-gradient(circle,rgba(88,101,242,.07) 0%,transparent 70%);
    pointer-events:none;
  }
  .dsc-bot:hover {
    border-color:rgba(88,101,242,.35);
    box-shadow:0 8px 56px rgba(88,101,242,.1);
  }
  .dsc-bot-top {
    display:flex;align-items:flex-start;justify-content:space-between;
    margin-bottom:1.3rem;
  }
  .dsc-bot-icon {
    width:54px;height:54px;border-radius:14px;
    background:rgba(88,101,242,.1);border:1px solid rgba(88,101,242,.22);
    display:flex;align-items:center;justify-content:center;font-size:1.6rem;
  }
  .dsc-bot-online {
    display:flex;align-items:center;gap:.4rem;
    font-family:var(--ff-mono);font-size:.64rem;
    color:var(--mint);font-weight:600;
    text-transform:uppercase;letter-spacing:.06em;
  }
  .dsc-bot-online::before {
    content:'';width:6px;height:6px;border-radius:50%;
    background:var(--mint);box-shadow:0 0 8px var(--mint);
  }
  .dsc-bot-name {
    font-family:var(--ff-display);font-size:1.55rem;
    letter-spacing:.05em;color:var(--t0);margin-bottom:.3rem;line-height:1;
  }
  .dsc-bot-role {
    font-family:var(--ff-mono);font-size:.66rem;
    color:var(--blurple2);text-transform:uppercase;
    letter-spacing:.08em;margin-bottom:1.1rem;
  }
  .dsc-bot-desc {
    font-size:.9rem;font-weight:300;color:var(--t2);
    line-height:1.72;margin-bottom:1.3rem;
  }
  .dsc-bot-tags { display:flex;flex-wrap:wrap;gap:.4rem; }
  .dsc-bot-tag {
    font-family:var(--ff-mono);font-size:.63rem;
    padding:.2rem .6rem;border-radius:6px;
    background:rgba(0,212,255,.06);color:var(--ice);
    border:1px solid rgba(0,212,255,.14);letter-spacing:.04em;
  }

  /* ── ABOUT / PILLARS ──────────────────────────────────────────────────── */
  .dsc-pillars { display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:1.2rem; }
  .dsc-pillar {
    padding:1.8rem;border-radius:16px;border:1px solid var(--rim);
    background:var(--surface);transition:border-color .25s,transform .25s;
  }
  .dsc-pillar:hover { border-color:rgba(88,101,242,.38);transform:translateY(-3px); }
  .dsc-pillar-icon {
    width:46px;height:46px;border-radius:12px;font-size:1.35rem;
    display:flex;align-items:center;justify-content:center;margin-bottom:1.1rem;
    background:rgba(88,101,242,.1);border:1px solid rgba(88,101,242,.22);
  }
  .dsc-pillar-title {
    font-family:var(--ff-display);font-size:1.4rem;letter-spacing:.04em;
    color:var(--t0);margin-bottom:.55rem;font-weight:400;line-height:1.05;
  }
  .dsc-pillar-text { font-size:.88rem;font-weight:300;color:var(--t2);line-height:1.72; }

  .dsc-steps-head {
    font-family:var(--ff-mono);font-size:.68rem;font-weight:600;color:var(--t3);
    text-transform:uppercase;letter-spacing:.14em;margin:3.6rem 0 1.2rem;
  }
  .dsc-steps {
    list-style:none;display:grid;gap:1rem;
    grid-template-columns:repeat(auto-fit,minmax(210px,1fr));
  }
  .dsc-step {
    padding:1.4rem 1.3rem;border-radius:14px;position:relative;
    border:1px solid var(--rim);background:var(--surface);
  }
  .dsc-step-n {
    font-family:var(--ff-display);font-size:2rem;line-height:1;letter-spacing:.05em;
    color:var(--blurple);display:block;margin-bottom:.6rem;
  }
  .dsc-step-title { font-size:.98rem;font-weight:600;color:var(--t0);margin-bottom:.4rem;line-height:1.3; }
  .dsc-step-text { font-size:.84rem;font-weight:300;color:var(--t2);line-height:1.65; }

  .dsc-portal-row {
    display:flex;flex-wrap:wrap;align-items:center;gap:1rem;margin-top:2.6rem;
  }
  .dsc-portal-url {
    font-family:var(--ff-mono);font-size:.74rem;color:var(--t3);
    text-decoration:none;letter-spacing:.03em;transition:color .2s;
  }
  .dsc-portal-url:hover { color:var(--ice); }

  /* ── COMMANDER ────────────────────────────────────────────────────────── */
  .dsc-cmd-layout { display:grid;grid-template-columns:minmax(0,1.05fr) minmax(0,1fr);gap:1.6rem;align-items:start; }
  .dsc-company {
    padding:1.8rem 1.4rem 1.5rem;border-radius:18px;
    border:1px solid rgba(245,200,66,.2);
    background:linear-gradient(180deg,rgba(245,200,66,.05),var(--surface) 55%);
    display:flex;flex-direction:column;align-items:center;
  }
  .dsc-company-label {
    font-family:var(--ff-mono);font-size:.64rem;font-weight:600;color:var(--gold);
    text-transform:uppercase;letter-spacing:.14em;margin-bottom:1.3rem;
  }
  .dsc-co-node {
    display:flex;align-items:center;gap:.65rem;text-align:left;
    padding:.6rem .85rem;border-radius:12px;
    background:var(--surface2);border:1px solid var(--rim2);
  }
  .dsc-co-node strong { display:block;font-size:.82rem;font-weight:600;color:var(--t0);line-height:1.2; }
  .dsc-co-node small  { display:block;font-family:var(--ff-mono);font-size:.58rem;color:var(--t3);margin-top:.25rem;letter-spacing:.03em; }
  .dsc-co-img { width:30px;height:30px;object-fit:contain;flex-shrink:0; }
  .dsc-co-img.sm { width:22px;height:22px; }
  .dsc-co-cmd { border-color:rgba(245,200,66,.4);background:rgba(245,200,66,.07);padding:.75rem 1.1rem; }
  .dsc-co-cmd strong { color:var(--gold);font-size:.95rem; }
  .dsc-co-pc  { border-color:rgba(167,139,250,.35);background:rgba(167,139,250,.07); }
  .dsc-co-tc  { border-color:rgba(96,165,250,.28);background:rgba(96,165,250,.06);padding:.45rem .6rem;gap:.45rem; }
  .dsc-co-tc strong { font-size:.7rem; }
  .dsc-co-line { width:1.5px;height:26px;background:linear-gradient(to bottom,rgba(245,200,66,.5),rgba(167,139,250,.5)); }
  .dsc-co-line.short { height:18px;background:linear-gradient(to bottom,rgba(167,139,250,.5),rgba(96,165,250,.45)); }
  .dsc-co-projects { display:grid;grid-template-columns:1fr 1fr;gap:1rem;width:100%; }
  .dsc-co-branch {
    display:flex;flex-direction:column;align-items:center;
    padding:.9rem .6rem;border-radius:14px;border:1px dashed var(--rim2);
  }
  .dsc-co-teams { display:flex;flex-direction:column;gap:.4rem;width:100%; }
  .dsc-co-teams .dsc-co-node { width:100%; }
  .dsc-co-note {
    margin-top:.8rem;font-family:var(--ff-mono);font-size:.58rem;color:var(--t3);
    text-align:center;line-height:1.5;letter-spacing:.03em;
  }
  .dsc-co-total { margin-top:1.2rem;font-size:.76rem;font-weight:300;color:var(--t3);text-align:center; }
  .dsc-co-total strong { color:var(--t2);font-weight:600; }

  .dsc-duties { display:flex;flex-direction:column;gap:.8rem; }
  .dsc-duty {
    display:grid;grid-template-columns:42px 1fr;gap:1rem;align-items:start;
    padding:1.15rem 1.2rem;border-radius:14px;
    border:1px solid var(--rim);background:var(--surface);
    transition:border-color .25s,transform .25s;
  }
  .dsc-duty:hover { border-color:rgba(245,200,66,.35);transform:translateX(5px); }
  .dsc-duty-icon {
    width:42px;height:42px;border-radius:11px;font-size:1.15rem;
    display:flex;align-items:center;justify-content:center;
    background:rgba(245,200,66,.08);border:1px solid rgba(245,200,66,.22);
  }
  .dsc-duty-title { font-size:.94rem;font-weight:600;color:var(--t0);margin-bottom:.3rem;line-height:1.3; }
  .dsc-duty-text { font-size:.82rem;font-weight:300;color:var(--t2);line-height:1.65; }

  .dsc-standards {
    margin-top:1.6rem;padding:1.6rem 1.8rem;border-radius:16px;
    border:1px solid var(--rim);background:var(--surface);
  }
  .dsc-standards-head {
    font-family:var(--ff-mono);font-size:.66rem;font-weight:600;color:var(--gold);
    text-transform:uppercase;letter-spacing:.14em;margin-bottom:1rem;
  }
  .dsc-standards-list { list-style:none;display:grid;gap:.7rem;grid-template-columns:repeat(auto-fit,minmax(300px,1fr)); }
  .dsc-standards-list li {
    position:relative;padding-left:1.4rem;
    font-size:.86rem;font-weight:300;color:var(--t2);line-height:1.65;
  }
  .dsc-standards-list li::before {
    content:'';position:absolute;left:0;top:.55em;width:7px;height:7px;border-radius:50%;
    background:var(--gold);box-shadow:0 0 10px rgba(245,200,66,.6);
  }

  /* ── WEEKLY FLOW ──────────────────────────────────────────────────────── */
  .dsc-flow {
    list-style:none;display:grid;gap:1rem;
    grid-template-columns:repeat(auto-fit,minmax(190px,1fr));
  }
  .dsc-flow-step {
    padding:1.4rem 1.3rem;border-radius:14px;position:relative;overflow:hidden;
    border:1px solid var(--rim);background:var(--surface);
  }
  .dsc-flow-step::before {
    content:'';position:absolute;top:0;left:0;right:0;height:2px;background:var(--tone);opacity:.8;
  }
  .dsc-flow-num {
    font-family:var(--ff-display);font-size:1.9rem;letter-spacing:.05em;line-height:1;
    color:var(--tone);display:block;margin-bottom:.6rem;
  }
  .dsc-flow-title { font-size:.98rem;font-weight:600;color:var(--t0);margin-bottom:.4rem;line-height:1.3; }
  .dsc-flow-text { font-size:.82rem;font-weight:300;color:var(--t2);line-height:1.65; }

  .dsc-yt {
    margin-top:1.6rem;padding:1.5rem 1.8rem;border-radius:16px;
    display:flex;align-items:center;justify-content:space-between;gap:1.2rem;flex-wrap:wrap;
    border:1px solid var(--rim);background:var(--surface);
  }
  .dsc-yt-title { font-family:var(--ff-display);font-size:1.5rem;letter-spacing:.04em;color:var(--t0);margin-bottom:.35rem; }
  .dsc-yt-text { font-size:.86rem;font-weight:300;color:var(--t2);line-height:1.65;max-width:520px; }

  /* ── SCORING TABLE ────────────────────────────────────────────────────── */
  .dsc-table-wrap { border:1px solid var(--rim);border-radius:16px;overflow:hidden;background:var(--surface); }
  .dsc-table { width:100%;border-collapse:collapse; }
  .dsc-table th {
    padding:.95rem 1.3rem;text-align:left;
    font-family:var(--ff-mono);font-size:.62rem;font-weight:600;color:var(--t3);
    text-transform:uppercase;letter-spacing:.12em;
    background:var(--surface2);border-bottom:1px solid var(--rim2);
  }
  .dsc-table td { padding:1.05rem 1.3rem;border-bottom:1px solid var(--rim);vertical-align:top; }
  .dsc-table tbody tr:last-child td { border-bottom:none; }
  .dsc-table tbody tr { transition:background .2s; }
  .dsc-table tbody tr:hover { background:rgba(88,101,242,.04); }
  .dsc-num { text-align:right !important;white-space:nowrap; }
  .dsc-td-act { font-size:.92rem;font-weight:600;color:var(--t0);white-space:nowrap; }
  .dsc-td-note { font-size:.84rem;font-weight:300;color:var(--t2);line-height:1.65; }
  .dsc-pts {
    display:inline-block;padding:.22rem .6rem;border-radius:8px;
    font-family:var(--ff-mono);font-size:.74rem;font-weight:600;color:var(--mint);
    background:rgba(35,209,139,.08);border:1px solid rgba(35,209,139,.22);
  }

  /* ── RANKS ────────────────────────────────────────────────────────────── */
  .dsc-ranks { display:grid;gap:.9rem;grid-template-columns:repeat(auto-fill,minmax(150px,1fr)); }
  .dsc-rank {
    display:flex;flex-direction:column;align-items:center;text-align:center;
    padding:1.2rem .8rem 1.1rem;border-radius:16px;position:relative;
    border:1px solid var(--rim);background:var(--surface);
    transition:border-color .25s,transform .25s,box-shadow .25s;
  }
  .dsc-rank::before {
    content:'';position:absolute;top:0;left:20%;right:20%;height:2px;border-radius:0 0 4px 4px;
    background:var(--rank-accent);opacity:.8;
  }
  .dsc-rank:hover {
    border-color:var(--rank-accent);transform:translateY(-4px);
    box-shadow:0 10px 40px rgba(0,0,0,.35);
  }
  .dsc-rank-tier {
    font-family:var(--ff-mono);font-size:.56rem;font-weight:600;color:var(--t3);
    text-transform:uppercase;letter-spacing:.12em;margin-bottom:.5rem;
  }
  .dsc-rank-img { width:84px;height:84px;object-fit:contain;margin-bottom:.6rem;filter:drop-shadow(0 4px 14px rgba(0,0,0,.45)); }
  .dsc-rank-name { font-family:var(--ff-display);font-size:1.25rem;letter-spacing:.05em;font-weight:400;color:var(--rank-accent);line-height:1; }
  .dsc-rank-band { font-family:var(--ff-mono);font-size:.6rem;color:var(--t2);margin-top:.45rem;letter-spacing:.02em; }
  .dsc-rank-div {
    margin-top:.5rem;padding:.14rem .5rem;border-radius:6px;
    font-family:var(--ff-mono);font-size:.54rem;color:var(--t3);letter-spacing:.08em;
    background:rgba(255,255,255,.04);border:1px solid var(--rim);
  }

  .dsc-season {
    margin-top:1.8rem;padding:1.8rem;border-radius:18px;
    display:grid;grid-template-columns:minmax(0,1.5fr) minmax(0,1fr);gap:2rem;align-items:center;
    border:1px solid rgba(88,101,242,.28);
    background:linear-gradient(135deg,rgba(88,101,242,.09),var(--surface) 60%);
  }
  .dsc-season-head { font-family:var(--ff-display);font-size:1.8rem;letter-spacing:.04em;color:var(--t0);margin-bottom:.6rem; }
  .dsc-season-copy p { font-size:.88rem;font-weight:300;color:var(--t2);line-height:1.75; }
  .dsc-season-copy strong { color:var(--t1);font-weight:600; }
  .dsc-season-grid { display:grid;grid-template-columns:1fr 1fr;gap:.7rem; }
  .dsc-season-card {
    display:flex;flex-direction:column;gap:.3rem;padding:.9rem 1rem;border-radius:12px;
    background:var(--surface2);border:1px solid var(--rim2);
  }
  .dsc-season-q { font-family:var(--ff-display);font-size:1.5rem;letter-spacing:.05em;color:var(--blurple2);line-height:1; }
  .dsc-season-m { font-family:var(--ff-mono);font-size:.64rem;color:var(--t2); }

  /* ── MEDALS ───────────────────────────────────────────────────────────── */
  .dsc-medals { display:grid;gap:1.2rem;grid-template-columns:repeat(auto-fit,minmax(280px,1fr)); }
  .dsc-medal {
    padding:1.8rem 1.6rem;border-radius:18px;text-align:center;
    border:1px solid var(--rim);background:var(--surface);
    display:flex;flex-direction:column;align-items:center;
    transition:border-color .25s,transform .25s;
  }
  .dsc-medal:hover { border-color:rgba(245,200,66,.38);transform:translateY(-4px); }
  .dsc-medal-period {
    font-family:var(--ff-mono);font-size:.62rem;font-weight:600;color:var(--gold);
    text-transform:uppercase;letter-spacing:.14em;margin-bottom:.4rem;
  }
  .dsc-medal-img { width:150px;height:100px;object-fit:contain;margin:.4rem 0 .6rem; }
  .dsc-medal-name { font-family:var(--ff-display);font-size:1.6rem;letter-spacing:.05em;color:var(--t0);line-height:1; }
  .dsc-medal-pts { font-family:var(--ff-mono);font-size:.72rem;font-weight:600;color:var(--mint);margin:.5rem 0 .8rem; }
  .dsc-medal-how { font-size:.84rem;font-weight:300;color:var(--t2);line-height:1.65;margin-bottom:1.2rem; }
  .dsc-medal-extra { list-style:none;width:100%;display:grid;gap:.45rem;margin-top:auto; }
  .dsc-medal-extra li {
    display:flex;align-items:center;justify-content:space-between;gap:.8rem;
    padding:.6rem .8rem;border-radius:10px;text-align:left;
    background:var(--surface2);border:1px solid var(--rim);
    font-size:.8rem;font-weight:500;color:var(--t1);
  }
  .dsc-medal-extra small { display:block;font-family:var(--ff-mono);font-size:.56rem;color:var(--t3);margin-top:.2rem;font-weight:400; }
  .dsc-medal-extra b { font-family:var(--ff-mono);font-size:.72rem;color:var(--mint);font-weight:600; }

  /* ── BOTTOM CTA ───────────────────────────────────────────────────────── */
  .dsc-bottom {
    position:relative;z-index:1;
    padding:7rem 1.5rem 9rem;text-align:center;overflow:hidden;
  }
  .dsc-bottom-orb {
    position:absolute;bottom:-120px;left:50%;transform:translateX(-50%);
    width:760px;height:430px;border-radius:50%;
    background:radial-gradient(ellipse,rgba(88,101,242,.18) 0%,transparent 70%);
    filter:blur(50px);pointer-events:none;
  }
  .dsc-bottom-inner { position:relative;z-index:1;max-width:620px;margin:0 auto; }

  .dsc-avatars {
    display:flex;align-items:center;justify-content:center;
    gap:0;margin-bottom:2rem;
  }
  .dsc-av {
    width:40px;height:40px;border-radius:50%;
    border:2px solid var(--paper);
    display:flex;align-items:center;justify-content:center;
    font-size:.9rem;font-weight:600;color:#fff;
    margin-left:-10px;flex-shrink:0;
  }
  .dsc-av:first-child { margin-left:0; }
  .dsc-av-k {
    background:var(--surface2);color:var(--t2);
    font-family:var(--ff-mono);font-size:.63rem;font-weight:600;
  }
  .dsc-av-label {
    margin-left:1rem;font-size:.9rem;color:var(--t2);font-weight:400;
  }
  .dsc-av-label strong { color:var(--mint);font-weight:700; }

  .dsc-bottom-h2 {
    font-family:var(--ff-display);
    font-size:clamp(3rem,8vw,6.5rem);
    letter-spacing:.02em;color:var(--t0);
    line-height:.92;margin-bottom:1.2rem;
  }
  .dsc-bottom-h2 .shimmer {
    background:linear-gradient(90deg,var(--blurple),var(--ice),var(--blurple2),var(--blurple));
    background-size:300% 100%;
    -webkit-background-clip:text;-webkit-text-fill-color:transparent;
    background-clip:text;
    animation:shimmer 4s linear infinite;
  }
  @keyframes shimmer {
    0%   { background-position:0% 0%; }
    100% { background-position:300% 0%; }
  }
  .dsc-bottom-sub {
    font-size:1.05rem;font-weight:300;color:var(--t2);
    line-height:1.78;margin-bottom:2.6rem;
  }

  /* ── Responsive ───────────────────────────────────────────────────────── */
  @media (max-width:640px) {
    .dsc-stats { grid-template-columns:1fr;border-radius:14px; }
    .dsc-stat  { border-right:none;border-bottom:1px solid var(--rim); }
    .dsc-stat:last-child { border-bottom:none; }
    .dsc-tier  { grid-template-columns:1fr; }
    .dsc-tier-num { display:none; }
  }
  @media (max-width:900px) {
    .dsc-cmd-layout { grid-template-columns:1fr; }
    .dsc-season { grid-template-columns:1fr; }
  }
  @media (max-width:640px) {
    .dsc-section { padding:4.5rem 1.25rem; }
    .dsc-co-projects { grid-template-columns:1fr; }
    .dsc-standards-list { grid-template-columns:1fr; }
    .dsc-ranks { grid-template-columns:repeat(auto-fill,minmax(130px,1fr)); }
    .dsc-table thead { display:none; }
    .dsc-table, .dsc-table tbody, .dsc-table tr, .dsc-table td { display:block; width:100%; }
    .dsc-table tr { padding:.9rem 1.1rem;border-bottom:1px solid var(--rim); }
    .dsc-table tbody tr:last-child { border-bottom:none; }
    .dsc-table td { padding:.15rem 0;border-bottom:none; }
    .dsc-table td.dsc-num { text-align:left !important;padding:.35rem 0 .45rem; }
    .dsc-td-act { white-space:normal; }
    .dsc-portal-row { flex-direction:column;align-items:stretch; }
    .dsc-portal-row .dsc-btn-join, .dsc-portal-row .dsc-btn-ghost { justify-content:center; }
    .dsc-portal-url { text-align:center; }
  }
`;
export default DiscordPageStyles;
